// ============================================================================
// USERS MANAGEMENT
// 
// Platform Admin: Create users via Cloud Function, full edit access
// Tenant Admin: Can only toggle status (active/disabled) for users in their tenant
// 
// User creation goes through Cloud Function to ensure:
// - Firebase Auth user is created
// - Firestore profile is created atomically
// - Custom claims are set
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { 
  User, Plus, Trash2, Edit2, Check, X, Loader2, 
  AlertCircle, Building, Shield, UserPlus, UserX, UserCheck,
  Info, Copy, Eye, EyeOff
} from 'lucide-react';
import {
  getUsers, getTenantUsers, saveUser, deleteUser, getTenants, getSpecialties,
  getStoreMode,
  ROLES, ROLE_DISPLAY, isPlatformAdmin, isTenantAdmin
} from '../core/index.js';
import { useAuth } from "../auth/AuthContext.jsx";

// All roles that a platform_admin can explicitly assign.
// 'pending' is excluded — it's a bootstrap/safety state, not a real role.
const ASSIGNABLE_ROLES = Object.entries(ROLE_DISPLAY).filter(
  ([role]) => role !== ROLES.PENDING
);


// Import Cloud Functions (only used in Firebase mode)
let createUserCF = null;
let deleteUserCF = null;
let updateUserStatusCF = null;
let updateUserClaimsCF = null;

// Dynamically import CF functions to avoid errors in local mode
const loadCloudFunctions = async () => {
  try {
    const module = await import('../core/functions.js');
    createUserCF = module.createUser;
    deleteUserCF = module.deleteUserCF;
    updateUserStatusCF = module.updateUserStatusCF;
    updateUserClaimsCF = module.updateUserClaimsCF;
  } catch (e) {
    console.warn('Cloud Functions not available:', e.message);
  }
};

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-slate-700/50">
          <h2 className="text-xl font-semibold text-white">{title}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
};

// Guided-learning focus highlight (same convention as the analytics views).
const focusHighlight = (target, activeFocusTarget) =>
  (target && activeFocusTarget === target ? ' ring-2 ring-cyan-400 animate-pulse rounded-xl' : '');

const UsersView = ({ activeFocusTarget = null }) => {
  const { user: currentUser, tenant: currentTenant } = useAuth();
  const isPlatAdmin = isPlatformAdmin(currentUser?.role);
  const isTenAdmin = isTenantAdmin(currentUser?.role);
  const isFirebaseMode = getStoreMode() === 'firebase';
  
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState({});
  const [tenantSpecialties, setTenantSpecialties] = useState({}); // Specialties keyed by tenantId
  const [loading, setLoading] = useState(true);
  const [loadingSpecialties, setLoadingSpecialties] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showCredentials, setShowCredentials] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState({ tenant: 'all', role: 'all', status: 'all' });
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: ROLES.GP_EMPLOYEE,
    tenantId: '',
    specialtyIds: [],
    status: 'active',
    tempPassword: '',
  });

  // Load Cloud Functions on mount
  useEffect(() => {
    if (isFirebaseMode) {
      loadCloudFunctions();
    }
  }, [isFirebaseMode]);

  const loadData = useCallback(async () => {
    try {
      let usersData;
      let tenantsData = {};
      
      if (isPlatAdmin) {
        [usersData, tenantsData] = await Promise.all([
          getUsers(),
          getTenants(),
        ]);
      } else {
        usersData = await getTenantUsers(currentUser.tenantId);
      }
      
      setUsers(Array.isArray(usersData) ? usersData : []);
      setTenants(tenantsData);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }, [isPlatAdmin, currentUser]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load specialties when tenant selection changes in form
  useEffect(() => {
    const loadTenantSpecialties = async () => {
      if (!formData.tenantId || tenantSpecialties[formData.tenantId]) return;
      
      setLoadingSpecialties(true);
      try {
        const specs = await getSpecialties(formData.tenantId);
        setTenantSpecialties(prev => ({
          ...prev,
          [formData.tenantId]: specs || {}
        }));
      } catch (err) {
        console.error('Error loading specialties:', err);
      } finally {
        setLoadingSpecialties(false);
      }
    };
    
    loadTenantSpecialties();
  }, [formData.tenantId, tenantSpecialties]);

  // Get available specialties for current tenant selection
  const getAvailableSpecialties = () => {
    if (!formData.tenantId) return [];
    const specs = tenantSpecialties[formData.tenantId] || {};
    return Object.entries(specs).map(([id, spec]) => ({
      id,
      name: spec.name || id,
    }));
  };

  const availableSpecialties = getAvailableSpecialties();

  // Platform Admin: Create new user via Cloud Function
  const handleCreate = async () => {
    if (!formData.email || !formData.name) {
      setError('Name and email are required');
      return;
    }

    if ((formData.role === ROLES.SPECIALIST || formData.role === ROLES.SPECIALIST_ADMIN) && formData.specialtyIds.length === 0) {
      setError('At least one specialty is required for specialist and specialist_admin roles');
      return;
    }

    if (formData.role !== ROLES.PLATFORM_ADMIN && !formData.tenantId) {
      setError('Tenant is required for non-platform roles');
      return;
    }

    setSaving(true);
    setError('');

    try {
      if (isFirebaseMode && createUserCF) {
        // Use Cloud Function in Firebase mode
        const result = await createUserCF({
          email: formData.email,
          name: formData.name,
          role: formData.role,
          tenantId: formData.tenantId || null,
          specialtyIds: (formData.role === ROLES.SPECIALIST || formData.role === ROLES.SPECIALIST_ADMIN) ? formData.specialtyIds : [],
          tempPassword: formData.tempPassword || undefined,
        });

        // Show credentials modal
        setShowCredentials({
          email: result.email,
          tempPassword: result.tempPassword,
          uid: result.uid,
        });
      } else {
        // Fallback for local mode (direct Firestore write)
        const newUserId = `user_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
        await saveUser({
          id: newUserId,
          email: formData.email,
          name: formData.name,
          role: formData.role,
          tenantId: formData.tenantId || null,
          specialtyIds: (formData.role === ROLES.SPECIALIST || formData.role === ROLES.SPECIALIST_ADMIN) ? formData.specialtyIds : [],
          status: 'active',
        });
      }

      setShowCreate(false);
      setFormData({ name: '', email: '', role: ROLES.GP_EMPLOYEE, tenantId: '', specialtyIds: [], status: 'active', tempPassword: '' });
      loadData();
    } catch (err) {
      console.error('Error creating user:', err);
      setError(safeUserError(err));
    } finally {
      setSaving(false);
    }
  };

  // Platform Admin: Update user
  const handleUpdate = async () => {
    if (!editUser) return;

    setSaving(true);
    setError('');

    try {
      const userId = editUser.id || editUser.uid;
      const newSpecialtyIds = (formData.role === ROLES.SPECIALIST || formData.role === ROLES.SPECIALIST_ADMIN) ? formData.specialtyIds : [];

      // Use CF to sync JWT claims when role or specialtyIds change
      const roleChanged = formData.role !== editUser.role;
      const specialtyChanged = JSON.stringify(newSpecialtyIds) !== JSON.stringify(editUser.specialtyIds || []);

      if (isFirebaseMode && updateUserClaimsCF && (roleChanged || specialtyChanged)) {
        await updateUserClaimsCF(userId, {
          role: roleChanged ? formData.role : undefined,
          specialtyIds: specialtyChanged ? newSpecialtyIds : undefined,
        });
        // Also update other non-claim fields via saveUser
        await saveUser({
          id: userId,
          ...formData,
          specialtyIds: newSpecialtyIds,
        });
      } else {
        await saveUser({
          id: userId,
          ...formData,
          specialtyIds: newSpecialtyIds,
        });
      }

      setEditUser(null);
      loadData();
    } catch (err) {
      setError(safeUserError(err));
    } finally {
      setSaving(false);
    }
  };

  // Toggle status (both platform admin and tenant admin can do this)
  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 'active' ? 'disabled' : 'active';
    
    try {
      if (isFirebaseMode && updateUserStatusCF) {
        await updateUserStatusCF(user.id || user.uid, newStatus);
      } else {
        await saveUser({ ...user, status: newStatus });
      }
      loadData();
    } catch (err) {
      console.error('Error toggling status:', err);
      alert('Failed to update user status: ' + safeUserError(err));
    }
  };

  // Platform Admin only: Delete user
  const handleDelete = async (userId) => {
    if (!confirm('Are you sure you want to delete this user? This cannot be undone.')) {
      return;
    }

    try {
      if (isFirebaseMode && deleteUserCF) {
        await deleteUserCF(userId);
      } else {
        await deleteUser(userId);
      }
      loadData();
    } catch (err) {
      console.error('Error deleting user:', err);
      alert('Failed to delete user: ' + safeUserError(err));
    }
  };

  const openEdit = (user) => {
    setEditUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      role: user.role || ROLES.GP_EMPLOYEE,
      tenantId: user.tenantId || '',
      specialtyIds: user.specialtyIds || [],
      status: user.status || 'active',
      tempPassword: '',
    });
    setError('');
  };

  const handleSpecialtyToggle = (specialtyId) => {
    setFormData(prev => {
      const current = prev.specialtyIds || [];
      if (current.includes(specialtyId)) {
        return { ...prev, specialtyIds: current.filter(id => id !== specialtyId) };
      } else {
        return { ...prev, specialtyIds: [...current, specialtyId] };
      }
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case ROLES.PLATFORM_ADMIN: return 'bg-amber-500/20 text-amber-400';
      case ROLES.TENANT_ADMIN: return 'bg-violet-500/20 text-violet-400';
      case ROLES.SPECIALIST:       return 'bg-blue-500/20 text-blue-400';
      case ROLES.SPECIALIST_ADMIN: return 'bg-sky-500/20 text-sky-400';
      default: return 'bg-emerald-500/20 text-emerald-400';
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Apply filters
  let filteredUsers = [...users];
  if (isPlatAdmin) {
    if (filter.tenant !== 'all') {
      if (filter.tenant === '') {
        filteredUsers = filteredUsers.filter(u => !u.tenantId);
      } else {
        filteredUsers = filteredUsers.filter(u => u.tenantId === filter.tenant);
      }
    }
    if (filter.role !== 'all') {
      filteredUsers = filteredUsers.filter(u => u.role === filter.role);
    }
  }
  if (filter.status !== 'all') {
    filteredUsers = filteredUsers.filter(u => u.status === filter.status);
  }

  const tenantList = Object.values(tenants);

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div data-learning-target="ta_staff_header" className={focusHighlight('ta_staff_header', activeFocusTarget).trim()}>
          <h2 className="text-2xl font-semibold text-white mb-2">
            {isPlatAdmin ? 'Users' : 'Staff Management'}
          </h2>
          <p className="text-slate-400">
            {isPlatAdmin 
              ? 'Manage user accounts and permissions' 
              : `Manage staff for ${currentTenant?.name || 'your organization'}`}
          </p>
        </div>
        
        {isPlatAdmin && (
          <button
            onClick={() => {
              setFormData({ 
                name: '', email: '', 
                role: ROLES.GP_EMPLOYEE, 
                tenantId: '',
                specialtyIds: [], 
                status: 'active',
                tempPassword: '',
              });
              setShowCreate(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-xl text-white font-medium transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            Add User
          </button>
        )}
      </div>

      {/* Tenant Admin Notice */}
      {isTenAdmin && (
        <div data-learning-target="ta_staff_invites" className={`flex items-start gap-3 p-4 mb-6 bg-violet-500/10 border border-violet-500/30 rounded-xl${focusHighlight('ta_staff_invites', activeFocusTarget)}`}>
          <Info className="w-5 h-5 text-violet-400 mt-0.5" />
          <div>
            <p className="text-sm text-violet-300 font-medium">Staff Management</p>
            <p className="text-xs text-violet-400/80 mt-1">
              You can activate or deactivate staff members. To add new staff, send a secure invite from Invites.
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        {isPlatAdmin && (
          <>
            <select
              value={filter.tenant}
              onChange={(e) => setFilter((prev) => ({ ...prev, tenant: e.target.value }))}
              className="bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-2 text-white text-sm"
            >
              <option value="all">All Tenants</option>
              <option value="">No Tenant (Platform)</option>
              {tenantList.map((t) => (
                <option key={t.tenantId || t.id} value={t.tenantId || t.id}>{t.name}</option>
              ))}
            </select>

            <select
              value={filter.role}
              onChange={(e) => setFilter((prev) => ({ ...prev, role: e.target.value }))}
              className="bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-2 text-white text-sm"
            >
              <option value="all">All Roles</option>
              {ASSIGNABLE_ROLES.map(([role, info]) => (
                <option key={role} value={role}>{info.name}</option>
              ))}
            </select>
          </>
        )}

        <select
          data-learning-target="ta_staff_status_filter"
          value={filter.status}
          onChange={(e) => setFilter((prev) => ({ ...prev, status: e.target.value }))}
          className={`bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-2 text-white text-sm${focusHighlight('ta_staff_status_filter', activeFocusTarget)}`}
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
        </select>
      </div>

      {/* Users Table */}
      <div data-learning-target="ta_staff_table" className={`bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden${focusHighlight('ta_staff_table', activeFocusTarget)}`}>
        <table className="w-full">
          <thead className="bg-slate-900/50">
            <tr>
              <th className="text-left text-xs font-medium text-slate-500 uppercase px-6 py-3">User</th>
              {isPlatAdmin && (
                <th className="text-left text-xs font-medium text-slate-500 uppercase px-6 py-3">Tenant</th>
              )}
              <th className="text-left text-xs font-medium text-slate-500 uppercase px-6 py-3">Role</th>
              <th className="text-left text-xs font-medium text-slate-500 uppercase px-6 py-3">Specialties</th>
              <th className="text-left text-xs font-medium text-slate-500 uppercase px-6 py-3">Status</th>
              <th className="text-right text-xs font-medium text-slate-500 uppercase px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50">
            {filteredUsers.map((user, userIdx) => (
              <tr key={user.id || user.uid} className="hover:bg-slate-800/30">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                      <User className="w-5 h-5 text-slate-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{user.name}</p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                    </div>
                  </div>
                </td>
                {isPlatAdmin && (
                  <td className="px-6 py-4">
                    {user.tenantId ? (
                      <div className="flex items-center gap-2">
                        <Building className="w-4 h-4 text-slate-500" />
                        <span className="text-sm text-slate-400">
                          {tenants[user.tenantId]?.name || user.tenantId}
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm text-amber-400">Platform</span>
                    )}
                  </td>
                )}
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                    {ROLE_DISPLAY[user.role]?.name || user.role}
                  </span>
                </td>
                <td className="px-6 py-4">
                  {user.specialtyIds && user.specialtyIds.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {user.specialtyIds.map(sid => (
                        <span key={sid} className="px-2 py-0.5 rounded bg-slate-700/50 text-xs text-slate-300">
                          {sid}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-slate-500">-</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <button
                    {...(userIdx === 0 ? { 'data-learning-target': 'ta_staff_status_button' } : {})}
                    onClick={() => handleToggleStatus(user)}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                      user.status === 'active'
                        ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
                        : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                    }${userIdx === 0 ? focusHighlight('ta_staff_status_button', activeFocusTarget) : ''}`}
                  >
                    {user.status === 'active' ? <UserCheck className="w-3 h-3" /> : <UserX className="w-3 h-3" />}
                    {user.status}
                  </button>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    {isPlatAdmin && (
                      <>
                        <button
                          onClick={() => openEdit(user)}
                          className="p-2 rounded-lg hover:bg-blue-500/20 text-slate-500 hover:text-blue-400"
                          title="Edit user"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(user.id || user.uid)}
                          className="p-2 rounded-lg hover:bg-red-500/20 text-slate-500 hover:text-red-400"
                          title="Delete user"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filteredUsers.length === 0 && (
              <tr>
                <td colSpan={isPlatAdmin ? 6 : 5} className="px-6 py-12 text-center text-slate-500">
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create User Modal */}
      {isPlatAdmin && (
        <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Add User">
          <div className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <AlertCircle className="w-4 h-4 text-red-400" />
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Dr. Jane Smith"
                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                placeholder="jane@hospital.org"
                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">Tenant</label>
              <select
                value={formData.tenantId}
                onChange={(e) => setFormData((prev) => ({ 
                  ...prev, 
                  tenantId: e.target.value,
                  specialtyIds: [] // Clear specialties when tenant changes
                }))}
                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white"
              >
                <option value="">No Tenant (Platform Admin only)</option>
                {tenantList.map((t) => (
                  <option key={t.tenantId || t.id} value={t.tenantId || t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">Role *</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value }))}
                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white"
              >
                {ASSIGNABLE_ROLES.map(([role, info]) => (
                  <option key={role} value={role}>{info.name}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500">{ROLE_DISPLAY[formData.role]?.description}</p>
            </div>

            {/* Specialty selector for specialists and specialist_admin */}
            {(formData.role === ROLES.SPECIALIST || formData.role === ROLES.SPECIALIST_ADMIN) && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-300">Specialties *</label>
                {!formData.tenantId ? (
                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-sm text-amber-400">Please select a tenant first to see available specialties.</p>
                  </div>
                ) : loadingSpecialties ? (
                  <div className="flex items-center justify-center p-4">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
                    <span className="ml-2 text-sm text-slate-400">Loading specialties...</span>
                  </div>
                ) : availableSpecialties.length === 0 ? (
                  <div className="p-4 bg-slate-800/50 border border-slate-700/50 rounded-lg">
                    <p className="text-sm text-slate-400">No specialties enabled for this tenant.</p>
                    <p className="text-xs text-slate-500 mt-1">Enable specialties in Pack Manager first.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                    {availableSpecialties.map((spec) => (
                      <label
                        key={spec.id}
                        className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                          formData.specialtyIds.includes(spec.id)
                            ? 'bg-blue-500/20 border-blue-500/50 text-blue-300'
                            : 'bg-slate-800/30 border-slate-700/50 text-slate-400 hover:border-slate-600'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.specialtyIds.includes(spec.id)}
                          onChange={() => handleSpecialtyToggle(spec.id)}
                          className="sr-only"
                        />
                        <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                          formData.specialtyIds.includes(spec.id)
                            ? 'bg-blue-500 border-blue-500'
                            : 'border-slate-600'
                        }`}>
                          {formData.specialtyIds.includes(spec.id) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <span className="text-sm">{spec.name}</span>
                      </label>
                    ))}
                  </div>
                )}
                <p className="text-xs text-slate-500">Select the specialties this user can access</p>
              </div>
            )}

            {/* Temporary password (optional) */}
            {isFirebaseMode && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-300">Temporary Password (optional)</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.tempPassword}
                    onChange={(e) => setFormData((prev) => ({ ...prev, tempPassword: e.target.value }))}
                    placeholder="Leave blank to generate"
                    className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 pr-12 text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">A random password will be generated if left blank</p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-slate-400 hover:text-white">
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={saving || !formData.name || !formData.email}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-700 rounded-lg text-white"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                Add User
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Credentials Modal (shown after user creation) */}
      <Modal isOpen={!!showCredentials} onClose={() => setShowCredentials(null)} title="User Created Successfully">
        <div className="space-y-4">
          <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
            <p className="text-sm text-emerald-300">
              The user has been created. Share these credentials securely with the user.
            </p>
          </div>

          <div className="space-y-3">
            <div className="space-y-1">
              <label className="block text-xs font-medium text-slate-500 uppercase">Email</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-slate-800/50 border border-slate-700/50 rounded px-3 py-2 text-sm text-white font-mono">
                  {showCredentials?.email}
                </code>
                <button
                  onClick={() => copyToClipboard(showCredentials?.email)}
                  className="p-2 rounded hover:bg-slate-700 text-slate-400 hover:text-white"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-1">
              <label className="block text-xs font-medium text-slate-500 uppercase">Temporary Password</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-slate-800/50 border border-slate-700/50 rounded px-3 py-2 text-sm text-white font-mono">
                  {showCredentials?.tempPassword}
                </code>
                <button
                  onClick={() => copyToClipboard(showCredentials?.tempPassword)}
                  className="p-2 rounded hover:bg-slate-700 text-slate-400 hover:text-white"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <p className="text-xs text-amber-300">
              ⚠️ This password will only be shown once. Make sure to save it securely.
            </p>
          </div>

          <div className="flex justify-end pt-4">
            <button
              onClick={() => setShowCredentials(null)}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white"
            >
              Done
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit User Modal */}
      {isPlatAdmin && (
        <Modal isOpen={!!editUser} onClose={() => setEditUser(null)} title="Edit User">
          <div className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <AlertCircle className="w-4 h-4 text-red-400" />
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">Tenant</label>
              <select
                value={formData.tenantId}
                onChange={(e) => setFormData((prev) => ({ ...prev, tenantId: e.target.value }))}
                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white"
              >
                <option value="">No Tenant (Platform Admin)</option>
                {tenantList.map((t) => (
                  <option key={t.tenantId || t.id} value={t.tenantId || t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value }))}
                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white"
              >
                {ASSIGNABLE_ROLES.map(([role, info]) => (
                  <option key={role} value={role}>{info.name}</option>
                ))}
              </select>
            </div>

            {/* Specialty selector for specialists and specialist_admin */}
            {(formData.role === ROLES.SPECIALIST || formData.role === ROLES.SPECIALIST_ADMIN) && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-300">Specialties *</label>
                {!formData.tenantId ? (
                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-sm text-amber-400">Please select a tenant first to see available specialties.</p>
                  </div>
                ) : loadingSpecialties ? (
                  <div className="flex items-center justify-center p-4">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
                    <span className="ml-2 text-sm text-slate-400">Loading specialties...</span>
                  </div>
                ) : availableSpecialties.length === 0 ? (
                  <div className="p-4 bg-slate-800/50 border border-slate-700/50 rounded-lg">
                    <p className="text-sm text-slate-400">No specialties enabled for this tenant.</p>
                    <p className="text-xs text-slate-500 mt-1">Enable specialties in Pack Manager first.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                    {availableSpecialties.map((spec) => (
                      <label
                        key={spec.id}
                        className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                          formData.specialtyIds.includes(spec.id)
                            ? 'bg-blue-500/20 border-blue-500/50 text-blue-300'
                            : 'bg-slate-800/30 border-slate-700/50 text-slate-400 hover:border-slate-600'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.specialtyIds.includes(spec.id)}
                          onChange={() => handleSpecialtyToggle(spec.id)}
                          className="sr-only"
                        />
                        <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                          formData.specialtyIds.includes(spec.id)
                            ? 'bg-blue-500 border-blue-500'
                            : 'border-slate-600'
                        }`}>
                          {formData.specialtyIds.includes(spec.id) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <span className="text-sm">{spec.name}</span>
                      </label>
                    ))}
                  </div>
                )}
                <p className="text-xs text-slate-500">Select the specialties this user can access</p>
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}
                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white"
              >
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button onClick={() => setEditUser(null)} className="px-4 py-2 text-slate-400 hover:text-white">
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-700 rounded-lg text-white"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Save
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default UsersView;
